import { lovable } from "@/integrations/lovable";
import { landingUrlFor, type EntryIntent } from "@/engine/state/entryIntent";

/**
 * Signing in with a social provider from wherever the visitor already is.
 *
 * The sign-in page is a stop on the way to the identity provider, not a
 * destination: a visitor who pressed "create a room" has already said what they
 * want, so the round trip starts from that press and comes back carrying it.
 * The page still exists for the emailed link, and for anybody who wants it.
 *
 * Resolves false when the round trip could not be started, so the caller can
 * fall back to the page rather than leave a pressed button doing nothing.
 *
 * `returnTo` is for the screens the intent cannot describe — the OAuth consent
 * page has to be come back to at its exact address, authorization and all.
 */
export async function startOAuthSignIn(
  provider: "google" | "apple" | "microsoft",
  intent: EntryIntent | null,
  returnTo?: string,
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const landing =
    returnTo ??
    (intent
      ? landingUrlFor(window.location.origin, intent)
      : `${window.location.origin}/home`);
  try {
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: landing,
    });
    if (result && "error" in result && result.error) return false;
    // A redirect leaves the page; the popup path is already signed in here, so
    // the landing URL — which carries the intent home — is opened by hand.
    if (result && "redirected" in result && result.redirected) return true;
    // Assigning the URL the tab is already on does nothing, and the visitor is
    // left on a page that will never resume the intent: reload instead.
    if (window.location.href === landing) window.location.reload();
    else window.location.assign(landing);
    return true;
  } catch {
    return false;
  }
}

export function startGoogleSignIn(
  intent: EntryIntent | null,
  returnTo?: string,
): Promise<boolean> {
  return startOAuthSignIn("google", intent, returnTo);
}

export function startAppleSignIn(
  intent: EntryIntent | null,
  returnTo?: string,
): Promise<boolean> {
  return startOAuthSignIn("apple", intent, returnTo);
}

export function startMicrosoftSignIn(
  intent: EntryIntent | null,
  returnTo?: string,
): Promise<boolean> {
  return startOAuthSignIn("microsoft", intent, returnTo);
}
